import React from "react";
import moment from "moment";
import { Form } from "@modules/formx-react";
import { useSnackbars, useTenant } from "@hooks";
import { Observer, useLocalStore, useObserver } from "mobx-react";
import {
  DialogActions,
  Divider,
  FormHelperText,
  Grid,
  makeStyles,
  Paper,
  Typography
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { useNavigation } from "@navigation";
import { PrimaryButton, RegularButton, Topbar } from "@components";
import { Date, FormxDebugger, RadioGroup, Text } from "@components/formx";
import { OrderCreateStore } from "./order-create-store";
import { OrderCreateData } from "./order-create-data";
import { FormErrors, FormStore } from "@modules/formx";
import { HlrOrderParams, Tenant } from "@bonliva-crm/shared";

export type OrderCreatePageProps = {
  itemName: string;
  initialValues?: OrderCreateData;
  onSubmit?: (params: HlrOrderParams) => void;
  onCancel?: () => void;
  customValidation?: (form: FormStore<OrderCreateData>) => FormErrors<OrderCreateData>;
  options?: {
    maxPlaceSymbolLength?: number;
  };
};

export const OrderCreatePage: React.FC<OrderCreatePageProps> = (props) => {
  const {
    initialValues,
    onSubmit,
    customValidation,
    itemName,
    onCancel,
    options = {}
  } = props;
  const formStore = useLocalStore(
    () => new OrderCreateStore(initialValues, itemName, onSubmit, customValidation)
  );

  const { showError } = useSnackbars();
  const navigation = useNavigation();
  const { tenant } = useTenant();
  const styles = useStyles({});
  const { form } = formStore;
  const { maxPlaceSymbolLength } = options;

  const isSendToKvkWithEmailHide =
    tenant === Tenant.WCC &&
    (itemName === "S-HLR Barn" ||
      itemName === "S-HLR Vuxen" ||
      itemName === "A-HLR Barn" ||
      itemName === "A-HLR Vuxen");

  const handleClose = () => {
    onCancel?.();
    navigation.close();
  };

  return useObserver(() => {
    navigation.lockNavigation("order_create_form", form.dirty);

    return (
      <Form store={form} allowUpdates>
        <Grid container spacing={2} style={{ padding: 16, marginTop: -16 }}>
          <FormxDebugger store={form} />

          <Grid item xs={12}>
            <Topbar>
              <Typography variant="h6">{locale.tickets.createTicket}</Typography>
            </Topbar>
            <Divider style={{ margin: "8px -16px 0" }} />
          </Grid>

          <Grid item xs={12}>
            <Paper style={{ padding: 16 }}>
              <Grid style={{ position: "relative" }}>
                <Text
                  name="title"
                  label={locale.tickets.text}
                  TextFieldProps={{
                    autoFocus: true,
                    multiline: true,
                    rows: 1,
                    className: styles.textField
                  }}
                />
                <Text
                  name="place"
                  label={locale.tickets.perfPlaceExt}
                  TextFieldProps={{
                    multiline: true,
                    className: styles.textField,
                    inputProps: {
                      maxLength: maxPlaceSymbolLength ?? undefined
                    }
                  }}
                  helperText={
                    maxPlaceSymbolLength ? locale.maxSymbols(maxPlaceSymbolLength) : null
                  }
                />
                <div style={{ marginTop: 16 }}>
                  <RadioGroup
                    label={`${locale.tickets.whoChargedProjects}?`}
                    name="project"
                    options={[
                      {
                        label: "Bonliva",
                        value: "Bonliva"
                      },
                      {
                        label: "Konsult",
                        value: "Konsult"
                      }
                    ]}
                  />
                </div>
                {form.values.project === "Bonliva" && (
                  <Text
                    name="projectInfo"
                    label={locale.tickets.whichProjectCharge}
                    TextFieldProps={{
                      multiline: true,
                      className: styles.textField
                    }}
                  />
                )}

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Date
                      name="date"
                      utc
                      label={locale.tickets.dueDate}
                      DatePickerProps={{
                        fullWidth: true,
                        clearable: !!form.values.date,
                        minDate: moment.utc().startOf("day").toDate()
                      }}
                    />
                    <FormHelperText>{locale.tickets.dueDateInfo}</FormHelperText>
                  </Grid>
                </Grid>

                {/*//if due date < 10 days force to add a reason*/}
                {formStore.dueDateWithInTenDays && (
                  <Text
                    name="dueDateReason"
                    label={locale.tickets.dueDateReason}
                    TextFieldProps={{
                      multiline: true,
                      className: styles.textField
                    }}
                  />
                )}
                <Text
                  name="comment"
                  label={locale.Comment}
                  TextFieldProps={{
                    multiline: true,
                    className: styles.textField,
                    helperText:
                      "Genom att lägga denna beställning av HLR-kurs ger du KVK rätt att självständigt boka in din konsult på en kurs fram till ticketens förfallodatum om kursens pris är samma som eller mindre än det vid var tid gällande schablonbelopp. Detta godkännande innebär att KVK därutöver har rätt att boka in din konsult utan att konsultera dig om det finns kostnadsfri avbokning minst fram till 1 vecka från kursstart. Om avbokningsvillkor ej finns eller är längre än 1 vecka från kursstart kommer KVK kontakta dig och söka ett godkännande innan bokningen läggs"
                  }}
                />
                {/*<div className={styles.assignWrap}>*/}
                {/*  <div className={styles.teamAssignSection}>*/}
                {/*    <SelectField*/}
                {/*      fullWidth*/}
                {/*      name="Team"*/}
                {/*      label="Team"*/}
                {/*      disabled*/}
                {/*      options={teamsOptions}*/}
                {/*      value={form.values.assignedTeam}*/}
                {/*    />*/}
                {/*  </div>*/}
                {/*</div>*/}
                {/*{form.values.consultant && (*/}
                {/*  <div>*/}
                {/*    {locale.Linked}:&nbsp;*/}
                {/*    <NodeRefList items={[form.values.consultant]} />*/}
                {/*  </div>*/}
                {/*)}*/}
              </Grid>

              <Divider style={{ margin: "8px -16px" }} />

              <Observer>
                {() => (
                  <DialogActions style={{ padding: "16px 0" }}>
                    <RegularButton style={{ marginLeft: "auto" }} onClick={handleClose}>
                      {locale.Cancel}
                    </RegularButton>

                    <PrimaryButton
                      disabled={!form.canSubmit}
                      loading={formStore.isSubmitting}
                      onClick={() => {
                        formStore
                          .submit({ sendEmail: false })
                          .then(() => {
                            navigation.unBlock("order_create_form");
                            navigation.close();
                          })
                          .catch((err) =>
                            showError(`Cannot save\nReason: ${err.toString()}`)
                          );
                      }}
                    >
                      {locale.SendToKvkWithoutEmail}
                    </PrimaryButton>

                    {!isSendToKvkWithEmailHide ? (
                      <PrimaryButton
                        disabled={!form.canSubmit}
                        loading={formStore.isSubmitting}
                        onClick={() => {
                          formStore
                            .submit({ sendEmail: true })
                            .then(() => {
                              navigation.unBlock("order_create_form");
                              navigation.close();
                            })
                            .catch((err) =>
                              showError(`Cannot save\nReason: ${err.toString()}`)
                            );
                        }}
                      >
                        {locale.SendToKvkWithEmail}
                      </PrimaryButton>
                    ) : null}
                  </DialogActions>
                )}
              </Observer>
            </Paper>
          </Grid>
        </Grid>
      </Form>
    );
  });
};

const useStyles = makeStyles({
  datePicker: {
    width: "15%"
  },
  textField: {
    width: "100%",
    marginBottom: "10px"
  },
  teamAssignSection: {
    display: "flex",
    width: "15%",
    paddingBottom: "20px"
  },
  assignWrap: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: "15px"
  }
});
